import React, { useMemo, useCallback, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { lpForm } from 'lp-form'
import { Input, Select, SubmitButton, Button } from 'lp-components'
import { Field, FormSection, formValueSelector } from 'redux-form'
import SearchFilterToolbar from './search-filter-toolbar'
import { get, isEmpty } from 'lodash'
import classnames from 'classnames'
import BodyClassName from 'react-body-classname'
import { ToggleCheckbox, DropdownCheckboxGroup, Tooltip } from 'components'
import { SearchIcon } from 'components/icons'
import { useCommunity } from 'utils'
import * as Types from 'types'

const propTypes = {
  change: PropTypes.func.isRequired,
  enableRemoveAll: PropTypes.bool,
  handleSubmit: PropTypes.func.isRequired,
  hideTypeToggle: PropTypes.bool,
  initialValues: PropTypes.object,
  form: PropTypes.string.isRequired,
  searchOptions: PropTypes.object.isRequired,
  searchType: PropTypes.bool,
  selectedFilters: PropTypes.object,
  submitting: PropTypes.bool.isRequired,
  searchOnTypeChange: PropTypes.bool,
  searchOnYearChange: PropTypes.bool,
  hasShownSearchResults: PropTypes.bool,
  documentType: PropTypes.array,
  query: PropTypes.string,
}

const defaultProps = {
  enableRemoveAll: false,
  hideTypeToggle: false,
  initialValues: {},
  selectedFilters: {},
  searchType: Types.SCHOOL_SEARCH_TYPE,
  searchOnTypeChange: false,
  searchOnYearChange: false,
  hasShownSearchResults: false,
  query: '',
}

function SearchForm({
  change,
  enableRemoveAll,
  handleSubmit,
  hideTypeToggle,
  initialValues,
  form: formName,
  searchOptions,
  searchType,
  selectedFilters,
  submitting,
  searchOnTypeChange,
  searchOnYearChange,
  hasShownSearchResults,
  documentType,
  query,
}) {
  const [filtersVisibleOnMobile, setFiltersVisibleOnMobile] = useState(false)
  const { teachingArtist, subdomain } = useCommunity()

  const teachingArtistEnabled = teachingArtist?.enabled

  const searchQueryPlaceholder =
    subdomain === 'sacramento'
      ? 'Key word search'
      : 'Search artlook by school, organization, teaching artist, program, instructor, or address'

  const appliedFilters = useMemo(() => {
    return get(initialValues, 'filters', {})
  }, [initialValues.filters])

  const removeAllFilters = useCallback(() => {
    change('filters', {})
    setTimeout(handleSubmit, 0)
  }, [])

  // Set default partner search types when switching to partner search
  useEffect(() => {
    if (searchType === Types.PARTNER_SEARCH_TYPE && isEmpty(documentType)) {
      const defaultPartnerTypes = Types.PARTNER_SEARCH_TYPES.filter(
        (pt) => teachingArtistEnabled || pt.key !== 'Teaching Artists'
      ).map((option) => option.value)

      change('filters.documentType', defaultPartnerTypes)
    }
  }, [searchType, documentType])

  return (
    <BodyClassName className={filtersVisibleOnMobile ? 'no-scroll' : ''}>
      <form onSubmit={handleSubmit} noValidate role="search">
        <div className="search-form">
          <div className="select-options">
            {!hideTypeToggle ? (
              <Field
                name="type"
                component={ToggleCheckbox}
                label={false}
                aria-label="Search Type"
                className="toggle-checkbox toggle-checkbox-options"
                onChange={() => {
                  change('filters', {})
                  if (!searchOnTypeChange) return
                  // Skip a tick to ensure that form values are updated
                  return setTimeout(handleSubmit, 0)
                }}
                data-cy="search-type"
              />
            ) : (
              <Button
                variant="link"
                onClick={() => change('type', Types.PARTNER_SEARCH_TYPE)}
                className="partner-search-button"
              >
                Search for Arts Providers
              </Button>
            )}
            {searchType === Types.PARTNER_SEARCH_TYPE &&
              teachingArtistEnabled && (
                <Field
                  name="filters.documentType"
                  options={Types.PARTNER_SEARCH_TYPES}
                  component={DropdownCheckboxGroup}
                  label={false}
                  aria-label="Partner and program types to include in search results"
                  onChange={() => {
                    if (!searchOnYearChange) return
                    setTimeout(handleSubmit, 0)
                  }}
                />
              )}
            <Field
              name="schoolYear"
              component={Select}
              label={false}
              aria-label="School Year"
              options={searchOptions.schoolYears}
              onChange={() => {
                if (!searchOnYearChange) return
                setTimeout(handleSubmit, 0)
              }}
              data-cy="school-year"
              className="year"
            />
          </div>
          <div className="search-bar">
            <Tooltip icon={<SearchIcon />} iconClassName="search-icon-tooltip">
              {query || searchQueryPlaceholder}
            </Tooltip>
            <Field
              name="query"
              component={Input}
              label={false}
              aria-label={searchQueryPlaceholder}
              placeholder={searchQueryPlaceholder}
            />
            <SubmitButton
              {...{ submitting }}
              style="alert"
              data-cy="search-button"
            >
              Search
            </SubmitButton>
          </div>
        </div>
        {!isEmpty(searchOptions) && (
          <section
            className={classnames('map-filters', {
              'is-active': filtersVisibleOnMobile,
            })}
          >
            <button
              type="button"
              className="link-black filter-toggle"
              aria-expanded={filtersVisibleOnMobile}
              aria-controls="filter-content"
              onClick={() => setFiltersVisibleOnMobile(true)}
            >
              Advanced Filters +
            </button>
            <div id="filter-content" className="filter-content-wrapper">
              <button
                type="button"
                className="link-black close"
                aria-label="Close Filters"
                onClick={() => setFiltersVisibleOnMobile(false)}
              >
                ×
              </button>
              <FormSection name="filters">
                <SearchFilterToolbar
                  filterOptions={searchOptions}
                  applyFilter={() => {
                    handleSubmit()
                    if (filtersVisibleOnMobile) setFiltersVisibleOnMobile(false)
                  }}
                  resetFilter={(filterName) => {
                    change('filters.' + filterName, '')
                    setTimeout(handleSubmit, 0)
                    if (filtersVisibleOnMobile) setFiltersVisibleOnMobile(false)
                  }}
                  selectedFilters={selectedFilters}
                  appliedFilters={appliedFilters}
                  searchType={searchType}
                  partnerSearchTypes={documentType}
                  formName={formName}
                  removeAllFilters={() => {
                    removeAllFilters()
                    if (filtersVisibleOnMobile) setFiltersVisibleOnMobile(false)
                  }}
                  enableRemoveAll={enableRemoveAll}
                  hasShownSearchResults={hasShownSearchResults}
                  teachingArtistEnabled={teachingArtistEnabled}
                />
              </FormSection>
            </div>
          </section>
        )}
      </form>
    </BodyClassName>
  )
}

SearchForm.propTypes = propTypes
SearchForm.defaultProps = defaultProps

function mapStateToProps(state, { name }) {
  const formSelector = formValueSelector(name)
  return {
    selectedFilters: formSelector(state, 'filters'),
    searchType: formSelector(state, 'type'),
    documentType: formSelector(state, 'filters.documentType') || [],
    query: formSelector(state, 'query'),
  }
}

export default compose(
  connect(mapStateToProps),
  lpForm({ name: 'search-form' })
)(SearchForm)
